// File: src/services/api/gedcomPartner.ts
// Pure helpers for GEDCOM 5.5.1 FAM-level union marker ↔ Pecktree `relationSubtype` mapping (#1089).
//
// GEDCOM mapping (Pecktree convention):
//   marriage    → MARR  (GEDCOM 5.5.1 standard — also the fallback for NULL/unknown)
//   engagement  → ENGA  (GEDCOM 5.5.1 standard)
//   partnership → _UNGD (Pecktree custom extension — unmarried/unregistered partnership)

import type { RelationTypePartner } from '../../types.ts';

/** Map a Pecktree partner-union subtype to the GEDCOM FAM-level tag that marks
 *  the union when no explicit dated event exists.
 *    marriage    → 'MARR'  (GEDCOM standard; also the default for NULL/unknown)
 *    engagement  → 'ENGA'  (GEDCOM standard)
 *    partnership → '_UNGD' (Pecktree custom extension — unmarried partnership)
 */
export function partnerSubtypeToGedcomTag(
  subtype: RelationTypePartner | string | undefined
): string {
  switch (subtype) {
    case 'partnership':
      return '_UNGD';
    case 'engagement':
      return 'ENGA';
    default:
      return 'MARR'; // marriage / NULL / unknown → GEDCOM default
  }
}

/** Determine the partner-union subtype from which FAM-level tags are present.
 *  Priority: _UNGD (partnership) > MARR (marriage) > ENGA (engagement).
 *  Returns undefined when none present (relationship stays NULL → renders as marriage). */
export function gedcomFamToPartnerSubtype(opts: {
  hasUngd: boolean;
  hasMarr: boolean;
  hasEnga: boolean;
}): RelationTypePartner | undefined {
  if (opts.hasUngd) return 'partnership';
  if (opts.hasMarr) return 'marriage';
  if (opts.hasEnga) return 'engagement';
  return undefined;
}
